import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import { motion } from "framer-motion";
import NavHeader from "../../components/NavHeader";
import ComponentLoader from "../../components/ComponentLoader";
import {
  getPilotCart,
  clearPilotCart,
  getPilotStore,
} from "../../utils/pilotUtils";
import { CommonContext } from "../../contexts/CommonContext";
import { AuthContext } from "../../contexts/AuthContext";
import { Capacitor } from "@capacitor/core";
import { createPilotOrder, getPilotBranchInfo } from "../../services/api";
import { buildPilotInstoreCashfreeOrderDetails } from "../../utils/pilotCashfreeOrder";
import { coerceLegacyPilotCartCustomizations } from "../../utils/pilotCustomizationShape";

const PaymentOption = ({ value, label, isSelected }) => (
  <FormControlLabel
    value={value}
    sx={{
      display: "flex",
      flexDirection: "row",
      justifyContent: "center",
      alignItems: "center",
      fontFamily: "RobotSlab",
      borderRadius: "7px",
      width: "100%",
      flex: 1,
      background: isSelected ? "#a4243d" : "none",
      color: isSelected ? "#FFF5E8" : "#2c2c2c",
      border: isSelected ? "none" : "1px solid #2c2c2c",
      transition: "background 0.3s",
      marginRight: "0px !important",
    }}
    control={
      <Radio
        sx={{
          color: "#2c2c2c",
          margin: "2px",
          "&.Mui-checked": {
            color: "#FFF5E8",
          },
        }}
      />
    }
    label={
      <Box
        sx={{
          fontSize: "12px",
          width: "100%",
          fontWeight: "500",
          marginRight: "15px !important",
          fontFamily: "RobotoSlab",
        }}
      >
        {label}
      </Box>
    }
  />
);

// Choose the most appropriate slot for \"now\"
const selectCurrentSlot = (slots) => {
  if (!Array.isArray(slots) || slots.length === 0) return null;

  const now = new Date();
  const currentHour = now.getHours();

  const available = slots.filter((slot) => !slot.isDisabled);
  if (available.length === 0) return null;

  // Prefer a slot whose startTime is <= currentHour and closest to now
  let best = null;
  let minDiff = Infinity;

  available.forEach((slot) => {
    if (typeof slot.startTime !== "number") return;
    const diff = currentHour - slot.startTime;
    if (diff >= 0 && diff < minDiff) {
      minDiff = diff;
      best = slot;
    }
  });

  // If nothing in the past/now, pick the next upcoming slot
  if (!best) {
    available.forEach((slot) => {
      if (typeof slot.startTime !== "number") return;
      const diff = slot.startTime - currentHour;
      if (diff >= 0 && diff < minDiff) {
        minDiff = diff;
        best = slot;
      }
    });
  }

  return best || available[0];
};

function InstoreCheckout() {
  const navigate = useNavigate();
  const { isDesktop, showSnackbar, showLoader, hideLoader } =
    useContext(CommonContext);
  const { isUserLoggedIn, getUserId } = useContext(AuthContext);

  const [loading, setLoading] = useState(true);
  const [cart, setCart] = useState({ products: [], totalAmount: 0, totalCount: 0 });
  const [storeDetails, setStoreDetails] = useState(null);
  const [slots, setSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [paymentMode, setPaymentMode] = useState("cod");
  const [placingOrder, setPlacingOrder] = useState(false);

  useEffect(() => {
    const init = async () => {
      try {
        setLoading(true);
        const [pilotCart, store] = await Promise.all([
          getPilotCart(),
          getPilotStore(),
        ]);

        if (!store) {
          showSnackbar(
            "Please visit our store and scan the QR code to order",
            "error"
          );
          navigate("/pilot/home", { replace: true });
          return;
        }

        if (!pilotCart || !pilotCart.totalCount) {
          showSnackbar("Your cart is empty", "info");
          navigate("/pilot/home", { replace: true });
          return;
        }

        setCart(pilotCart);
        setStoreDetails(store);

        showLoader();
        const branchInfo = await getPilotBranchInfo({
          userId: await getUserId(),
          branchId: store.branchId,
          deliveryDate: "Today",
          timeStamp: Date.now(),
          storeId: store.storeId || store.id,
          podId: store.podId,
          cartData: pilotCart.products.map((p) => p.productId),
        });

        const branchSlots = branchInfo?.slots || [];
        setSlots(branchSlots);
        const autoSlot = selectCurrentSlot(branchSlots);
        setSelectedSlot(autoSlot || null);
      } catch (error) {
        console.error("Error initialising pilot checkout:", error);
        showSnackbar(
          error?.message || "Unable to load checkout. Please try again.",
          "error"
        );
        navigate("/pilot/home", { replace: true });
      } finally {
        hideLoader();
        setLoading(false);
      }
    };

    init();
    // We intentionally run this only once on mount to avoid
    // repeatedly calling the time-slots API.
  }, []);

  const buildPilotOrderPayload = async () => {
    const userId = await getUserId();
    const mobile = String(userId).trim();
    return {
      userId,
      pranaCustomerId: mobile,
      storeId: storeDetails.id || null,
      storeDetails,
      timeSlotId: selectedSlot.id,
      timeSlotDetails: {
        startTime: selectedSlot.startTime,
        time: selectedSlot.time,
        delTime: selectedSlot.delTime,
        id: selectedSlot.id,
        isDisabled: selectedSlot.isDisabled,
      },
      products: cart.products.map((item) => ({
        productId: item.productId,
        name: item.name,
        imageUrl: item.imageUrl || null,
        quantity: item.quantity,
        price: item.price,
        variant: item.variant || {},
        customizations: coerceLegacyPilotCartCustomizations(
          item.customizations || {}
        ),
      })),
      subtotal: parseFloat(cart.totalAmount || 0),
      shippingCost: 0,
      discount: 0,
      total: parseFloat(cart.totalAmount || 0),
      appliedCoupon: null,
      instructions: null,
      deliveryDate: new Date().toISOString().split("T")[0],
      deliveryDateTimestamp: Date.now(),
      branch_id: storeDetails.branchId || null,
      status: "pending",
      orderType: "instore_order",
      createdBy: "customer",
      paymentStatus: "pending",
      paymentMode,
      platform: Capacitor.getPlatform(),
      /* Pilot createOrder: no transactionRef → cash_on_delivery (same as COD) */
      deliveryType: "In Store",
    };
  };

  const handleConfirmOrder = async () => {
    try {
      if (!cart.totalCount) {
        showSnackbar("Your cart is empty", "error");
        return;
      }

      if (!storeDetails) {
        showSnackbar(
          "Store details missing. Please rescan the QR code in store.",
          "error"
        );
        return;
      }

      if (!selectedSlot) {
        showSnackbar("No valid time slot available for now", "error");
        return;
      }

      if (!paymentMode) {
        showSnackbar("Please select a payment mode", "error");
        return;
      }

      if (!(await isUserLoggedIn())) {
        showSnackbar("Please login to place your order", "info");
        navigate("/pilot/login", {
          state: { redirectTo: "/pilot/delivery" },
        });
        return;
      }

      setPlacingOrder(true);
      showLoader();

      const pilotOrderData = await buildPilotOrderPayload();

      if (paymentMode === "online") {
        navigate("/pilot/make-payment", {
          state: { pilotOrderPayload: pilotOrderData },
        });
        return;
      }

      /* Cash at store = COD: create order on Pilot without Cashfree */
      const result = await createPilotOrder(pilotOrderData);
      await clearPilotCart();

      showSnackbar("Your order has been placed successfully!", "success");

      const orderId =
        result?.order?.orderId ?? result?.order?.id ?? result?.orderId;
      const { userId } = pilotOrderData;
      const { itemDetails } = buildPilotInstoreCashfreeOrderDetails({
        userId,
        pilotOrderPayload: pilotOrderData,
      });

      navigate("/orderStatus", {
        state: {
          orderId,
          orderData: { userId, itemDetails },
        },
        replace: true,
      });
    } catch (error) {
      console.error("Pilot in-store order failed:", error);
      showSnackbar(
        error?.message || "Failed to place order. Please try again.",
        "error"
      );
    } finally {
      hideLoader();
      setPlacingOrder(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          padding: isDesktop ? "4vw 10vw" : "4vw",
          marginTop: "7vh",
          minHeight: "100vh",
        }}
      >
        <ComponentLoader />
      </Box>
    );
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <Box
        sx={{
          maxWidth: "700px",
          marginTop: isDesktop ? "14vh" : "7vh",
          marginLeft: isDesktop ? "50px" : "",
          padding: "0 20px 40px 20px",
        }}
      >
        <NavHeader />

        <Box sx={{ mt: 2, mb: 2 }}>
          <Typography
            sx={{
              fontSize: "24px",
              fontFamily: "Foregen",
              color: "#404040",
              textDecoration: "underline",
              textDecorationColor: "#a4243d",
            }}
          >
            In-Store Order Summary
          </Typography>
        </Box>

        {storeDetails && (
          <Paper
            elevation={0}
            sx={{
              padding: "16px 20px",
              borderRadius: "12px",
              background:
                "linear-gradient(135deg, rgba(164, 36, 61, 0.08) 0%, rgba(255, 107, 107, 0.08) 100%)",
              border: "1px solid rgba(164, 36, 61, 0.2)",
              mb: 3,
            }}
          >
            <Typography
              sx={{
                fontSize: "12px",
                fontWeight: "600",
                color: "#6c757d",
                fontFamily: "RobotoSlab",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
            >
              Ordering from
            </Typography>
            <Typography
              sx={{
                fontSize: "18px",
                fontWeight: "700",
                color: "#2d3436",
                fontFamily: "RobotoSlabBold",
              }}
            >
              {storeDetails.storeName}
            </Typography>
            {storeDetails.address && (
              <Typography
                sx={{
                  fontSize: "13px",
                  color: "#6c757d",
                  mt: 0.5,
                  fontFamily: "RobotoSlab",
                }}
              >
                {storeDetails.address}
              </Typography>
            )}
          </Paper>
        )}

        <Paper
          elevation={0}
          sx={{
            padding: "16px 20px",
            borderRadius: "12px",
            backgroundColor: "#FFF7E9",
            mb: 3,
          }}
        >
          <Typography
            sx={{
              fontSize: "18px",
              fontWeight: "700",
              fontFamily: "RobotoSlabBold",
              mb: 1,
            }}
          >
            Order Summary
          </Typography>
          <Divider sx={{ mb: 2 }} />
          {cart.products.map((item, index) => (
            <Box
              key={index}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 1.5,
              }}
            >
              <Box>
                <Typography
                  sx={{
                    fontSize: "15px",
                    fontWeight: "600",
                    fontFamily: "RobotoSlab",
                  }}
                >
                  {item.name}
                </Typography>
                <Typography
                  sx={{
                    fontSize: "13px",
                    color: "#6c757d",
                    fontFamily: "RobotoSlab",
                  }}
                >
                  Qty: {item.quantity}
                </Typography>
              </Box>
              <Typography
                sx={{
                  fontSize: "15px",
                  fontWeight: "700",
                  fontFamily: "RobotoSlabBold",
                  color: "#a4243d",
                }}
              >
                ₹{(item.price * item.quantity).toFixed(2)}
              </Typography>
            </Box>
          ))}
          <Divider sx={{ mt: 1.5, mb: 1.5 }} />
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography
              sx={{
                fontSize: "16px",
                fontWeight: "700",
                fontFamily: "RobotoSlabBold",
              }}
            >
              Total
            </Typography>
            <Typography
              sx={{
                fontSize: "20px",
                fontWeight: "800",
                fontFamily: "RobotoSlabBold",
                color: "#a4243d",
              }}
            >
              ₹{cart.totalAmount.toFixed(2)}
            </Typography>
          </Box>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            padding: "16px 20px",
            borderRadius: "12px",
            backgroundColor: "#FFFFFF",
            mb: 3,
          }}
        >
          <Typography
            sx={{
              fontSize: "16px",
              fontWeight: "700",
              fontFamily: "RobotoSlabBold",
              mb: 1,
            }}
          >
            Payment Mode
          </Typography>
          <FormControl style={{ width: "max-content" }}>
            <RadioGroup
              value={paymentMode}
              onChange={(e) => setPaymentMode(e.target.value)}
              sx={{ color: "#FFF5E8" }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  gap: 3,
                  flexWrap: "wrap",
                  background: "#FFF5E8",
                  borderRadius: "5px",
                  fontFamily: "RobotoSlab",
                  padding: "4px",
                }}
              >
                <PaymentOption
                  value="cod"
                  label="Cash at store"
                  isSelected={paymentMode === "cod"}
                />
                <PaymentOption
                  value="online"
                  label="Online payment"
                  isSelected={paymentMode === "online"}
                />
              </Box>
            </RadioGroup>
          </FormControl>
        </Paper>

        <Box
          sx={{
            backgroundColor: "#a4243d",
            display: "flex",
            flexDirection: "row",
            justifyContent: "center",
            position: "relative",
            padding: "20px",
            borderRadius: "5px",
            marginBottom: "20px",
          }}
        >
          <Box
            sx={{
              backgroundColor: "#a4243d",
              alignItems: "center",
              width: "100%",
            }}
          >
            <Button
              onClick={handleConfirmOrder}
              variant="outlined"
              fullWidth
              disabled={placingOrder}
              style={{
                background: "#fff5e8",
                fontFamily: "RobotoSlab",
                fontWeight: "bold",
              }}
            >
              {placingOrder ? "Placing Order..." : "Confirm Order"}
            </Button>
          </Box>
        </Box>
      </Box>
    </motion.div>
  );
}

export default InstoreCheckout;

